import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRightLeft, faRotateLeft, faTriangleExclamation, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import './SessionBalance.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const NAME = { standard: 'Standard', mastery: 'Mastery' };
const money = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : `$${Number(n).toFixed(2).replace(/\.00$/, '')}`);
const other = (t) => (t === 'standard' ? 'mastery' : 'standard');

/* Payments tab: sessions left of each kind (Standard / Mastery & Robotics), and converting
   them with the same value (fewer sessions): 24 Standard x $12.50 = $300 -> 18 Mastery at $16.25 + $7.50 credit. */
/* compact (Overview tab): just the two boxes; "Convert" opens the Payments tab (onOpenPayments) */
const SessionBalance = ({ studentId, reloadKey, compact = false, onOpenPayments, onLoad }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null); // { from, to, sessions, fromPrice, toPrice, note }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/studentprofile/sessiontypes/${studentId}`, auth());
      setData(res.data);
      if (onLoad) onLoad(res.data); // Overview: "Sessions left to schedule" per type
    } catch {
      setData({ failed: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const openConvert = (from) => {
    const to = other(from);
    setMsg(null);
    setForm({
      from,
      to,
      sessions: Math.max(0, data.types[from].left) || '',
      fromPrice: data.suggest?.[from] ?? '',
      toPrice: data.suggest?.[to] ?? '',
      note: '',
    });
  };

  const n = Number(form?.sessions) || 0;
  const fp = Number(form?.fromPrice) || 0;
  const tp = Number(form?.toPrice) || 0;
  const value = Math.round(n * fp * 100) / 100;
  const toSessions = tp > 0 ? Math.floor((value + 1e-9) / tp) : 0;
  const credit = Math.round((value - toSessions * tp) * 100) / 100;

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await axios.post(`${API}/api/admin/studentprofile/convertsessions/${studentId}`, form, auth());
      setForm(null);
      setMsg({ ok: true, text: `Converted: ${n} ${NAME[form.from]} → ${toSessions} ${NAME[form.to]}${credit > 0 ? ` · ${money(credit)} credit to take off the next payment` : ''}.` });
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Couldn’t convert.' });
    } finally {
      setBusy(false);
    }
  };

  const undo = async (c) => {
    if (!window.confirm(`Undo: ${c.fromSessions} ${NAME[c.from]} → ${c.toSessions} ${NAME[c.to]}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/studentprofile/convertsessions/${c.id}`, auth());
      load();
    } catch {
      setMsg({ ok: false, text: 'Couldn’t undo it.' });
    }
  };

  if (!data) return null;
  if (data.failed) return null;

  return (
    <div className={`rmsbal${compact ? ' is-compact' : ''}`}>
      {!data.ready && <p className="rmsbal-note is-bad">Run SessionTypes.sql on the server: until then every session counts as Standard.</p>}
      <div className="rmsbal-types">
        {['standard', 'mastery'].map((t) => {
          const x = data.types[t];
          const current = data.currentType === t;
          return (
            <div key={t} className={`rmsbal-type is-${t} ${x.left < 0 ? 'is-over' : ''}`}>
              <span className="rmsbal-label">
                {t === 'standard' ? 'Standard' : 'Mastery & Robotics'}
                {current && data.currentCourse && <em> · now: {data.currentCourse}</em>}
              </span>
              <strong>{x.left}</strong>
              <small>
                left · {x.bought} bought · {x.used} done · {x.upcoming} scheduled
                {x.price != null ? ` · ${money(x.price)} each` : ''}
              </small>
              {x.left < 0 && (
                <small className="rmsbal-warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> {Math.abs(x.left)} {NAME[t]} {Math.abs(x.left) === 1 ? 'class' : 'classes'} not paid for
                </small>
              )}
              {x.left > 0 && data.ready && (!compact || onOpenPayments) && (
                <button type="button" className="rmsbal-convert" onClick={() => (compact ? onOpenPayments() : openConvert(t))}>
                  <FontAwesomeIcon icon={faRightLeft} /> Convert to {NAME[other(t)]}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {msg && (
        <p className={`rmsbal-note ${msg.ok ? '' : 'is-bad'}`}>
          <FontAwesomeIcon icon={msg.ok ? faCircleCheck : faTriangleExclamation} /> {msg.text}
        </p>
      )}

      {form && (
        <div className="rmsbal-form">
          <p className="rmsbal-form-title">
            Convert {NAME[form.from]} sessions to {NAME[form.to]} (same value, fewer sessions)
          </p>
          <div className="rmsbal-grid">
            <label>
              <span>{NAME[form.from]} sessions to convert</span>
              <input type="number" min="1" step="1" value={form.sessions} onChange={(e) => setForm({ ...form, sessions: e.target.value })} />
            </label>
            <label>
              <span>{NAME[form.from]} price per session ($)</span>
              <input type="number" min="0" step="0.01" value={form.fromPrice} onChange={(e) => setForm({ ...form, fromPrice: e.target.value })} />
            </label>
            <label>
              <span>{NAME[form.to]} price per session ($)</span>
              <input type="number" min="0" step="0.01" value={form.toPrice} onChange={(e) => setForm({ ...form, toPrice: e.target.value })} />
            </label>
            <label>
              <span>Note (optional)</span>
              <input type="text" maxLength={300} value={form.note} placeholder="Switching to AI Creators" onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
          </div>
          <p className="rmsbal-result">
            {n} × {money(fp)} = <b>{money(value)}</b> → <b>{toSessions} {NAME[form.to]}</b> sessions at {money(tp)}
            {credit > 0 && (
              <>
                {' '}
                · <b>{money(credit)} credit</b> (take it off their next payment)
              </>
            )}
          </p>
          {n > data.types[form.from].left && <p className="rmsbal-note is-bad">They only have {data.types[form.from].left} {NAME[form.from]} sessions left.</p>}
          <div className="rmsbal-actions">
            <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setForm(null)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="rmasp-btn" onClick={save} disabled={busy || toSessions < 1}>
              {busy ? 'Converting…' : `Convert to ${toSessions} ${NAME[form.to]}`}
            </button>
          </div>
        </div>
      )}

      {!compact && data.conversions?.length > 0 && (
        <ul className="rmsbal-history">
          {data.conversions.map((c) => (
            <li key={c.id}>
              <span>
                {new Date(c.at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}: {c.fromSessions} {NAME[c.from]} ({money(c.fromPrice)}) →{' '}
                {c.toSessions} {NAME[c.to]} ({money(c.toPrice)}){c.credit > 0 ? ` · ${money(c.credit)} credit` : ''}
                {c.note ? ` · “${c.note}”` : ''}
              </span>
              <button type="button" onClick={() => undo(c)} aria-label="Undo this conversion" title="Undo">
                <FontAwesomeIcon icon={faRotateLeft} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SessionBalance;
